// A persistent client cache for Chat (CHAT-PERF): the TanStack Query persister writes the
// chat lists to IndexedDB, so a reload or a switch back to a known channel renders from cache
// at once and then revalidates (restored queries keep their age, so the 30 s staleTime decides
// whether they refetch on mount).
//
// What is kept: the channel and space lists, the caller's channel settings, each channel's
// topics and header, and the last `PERSIST_MESSAGES` messages of the `PERSIST_THREADS` threads
// opened most recently. WORK-PERF adds Work: the spaces tree, the board list, My tasks, and the
// `PERSIST_BOARDS` boards loaded most recently (each board, its views and fields, and its saved
// views' tasks), so a reload or a return to a known board renders at once. Nothing else of the
// cache (no people, no `/me`, no task drawer), and never an optimistic row that the server has
// not confirmed.
//
// Whose cache: the stored copy carries the signed-in user's id as its `buster`; it is restored
// only once `/me` names the same user, and is thrown away otherwise. Sign-out and an expired
// session clear the query cache, which the subscription then writes back empty.
//
// Failure is quiet by design: no IndexedDB (private windows, tests, blocked storage), a failed
// open, a quota error or a corrupt entry all leave the app working exactly as without a
// persister. Only the HTTP client persists; the in-memory fake starts empty on every load.

import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { DehydratedState, Query, QueryClient, QueryKey } from "@tanstack/react-query";
import {
  type AsyncStorage,
  type PersistedClient,
  persistQueryClientRestore,
  persistQueryClientSubscribe,
} from "@tanstack/react-query-persist-client";

/** Messages kept per thread. */
export const PERSIST_MESSAGES = 50;
/** Threads (topics, DMs) kept, the most recently opened first. */
export const PERSIST_THREADS = 12;
/** A stored cache older than this is not restored. */
export const PERSIST_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const CHAT_LISTS = new Set(["spaces", "channels", "prefs", "channel", "topics", "messages"]);

/** Boards kept (WORK-PERF), the most recently loaded first. */
export const PERSIST_BOARDS = 8;
/** Work's lists kept whole: the tree, the board list and My tasks. */
const WORK_LISTS = new Set(["spaces", "boards", "space", "my-tasks"]);
/** A board's keys kept (`["company", co, "work", "board", id, …rest]`): itself, views, fields,
 * and its saved views' results; not its every-task list (unsaved drafts only). */
const boardPart = (key: QueryKey) =>
  key.length === 5 || key[5] === "views" || key[5] === "fields" || key[5] === "tasks";

/** Keys worth keeping: chat lists, Work's tree and My tasks, and recently opened boards. */
export function persistable(key: QueryKey): boolean {
  if (key[0] !== "company") return false;
  if (key[2] === "chat") return CHAT_LISTS.has(String(key[3]));
  if (key[2] === "work")
    return WORK_LISTS.has(String(key[3])) || (key[3] === "board" && boardPart(key));
  return false;
}

type Rows = { data?: unknown[] } & Record<string, unknown>;
type Paged = { pages?: Rows[]; pageParams?: unknown[] };

const confirmed = (rows: unknown[]) =>
  rows.filter(
    (r) => !(r && typeof r === "object" && "local" in r && (r as { local?: unknown }).local),
  );

/** One message list cut to its newest `PERSIST_MESSAGES` confirmed rows (newest page only). */
function trimMessages(data: unknown): unknown {
  if (!data || typeof data !== "object") return data;
  const paged = data as Paged;
  if (Array.isArray(paged.pages)) {
    const first = paged.pages[0];
    if (!first) return data;
    return {
      pages: [{ ...first, data: confirmed(first.data ?? []).slice(-PERSIST_MESSAGES) }],
      pageParams: (paged.pageParams ?? []).slice(0, 1),
    };
  }
  const page = data as Rows;
  return Array.isArray(page.data)
    ? { ...page, data: confirmed(page.data).slice(-PERSIST_MESSAGES) }
    : data;
}

/**
 * The part of a dehydrated cache that is stored: chat lists, and the messages of the most
 * recently updated threads only, each trimmed.
 */
export function trimForPersist(state: DehydratedState): DehydratedState {
  const queries = state.queries.filter((q) => persistable(q.queryKey));
  const threads = [
    ...new Set(
      queries
        .filter((q) => q.queryKey[3] === "messages")
        .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
        .map((q) => String(q.queryKey[4])),
    ),
  ].slice(0, PERSIST_THREADS);
  const keep = new Set(threads);
  // WORK-PERF: the boards whose saved-view results loaded most recently.
  const boards = new Set(
    [
      ...new Set(
        queries
          .filter(
            (q) =>
              q.queryKey[2] === "work" && q.queryKey[3] === "board" && q.queryKey[5] === "tasks",
          )
          .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
          .map((q) => String(q.queryKey[4])),
      ),
    ].slice(0, PERSIST_BOARDS),
  );
  return {
    mutations: [],
    queries: queries
      .filter((q) => q.queryKey[3] !== "messages" || keep.has(String(q.queryKey[4])))
      .filter(
        (q) =>
          q.queryKey[2] !== "work" ||
          q.queryKey[3] !== "board" ||
          boards.has(String(q.queryKey[4])),
      )
      .map((q) =>
        q.queryKey[3] === "messages"
          ? { ...q, state: { ...q.state, data: trimMessages(q.state.data) } }
          : q.queryKey[2] === "work"
            ? { ...q, state: { ...q.state, data: confirmedTasks(q.state.data) } }
            : q,
      ),
  };
}

/** A Work list without the cards the client made up (an optimistic create not yet answered). */
function confirmedTasks(data: unknown): unknown {
  if (!data || typeof data !== "object") return data;
  const d = data as Rows & { groups?: (Rows & { data: unknown[] })[] | null };
  if (!Array.isArray(d.data)) return data;
  return {
    ...d,
    data: confirmed(d.data),
    ...(Array.isArray(d.groups)
      ? { groups: d.groups.map((g) => ({ ...g, data: confirmed(g.data) })) }
      : {}),
  };
}

/** IndexedDB as the persister's async key-value storage; null when it is unavailable. */
export function indexedDbStorage(
  dbName = "companyos-cache",
  storeName = "query",
): AsyncStorage<string> | null {
  if (typeof indexedDB === "undefined") return null;
  let opened: Promise<IDBDatabase | null> | null = null;
  const open = () => {
    opened ??= new Promise<IDBDatabase | null>((resolve) => {
      try {
        const req = indexedDB.open(dbName, 1);
        req.onupgradeneeded = () => {
          if (!req.result.objectStoreNames.contains(storeName))
            req.result.createObjectStore(storeName);
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    return opened;
  };
  const run = async <T>(
    mode: IDBTransactionMode,
    op: (s: IDBObjectStore) => IDBRequest,
  ): Promise<T | null> => {
    const db = await open();
    if (!db) return null;
    return new Promise<T | null>((resolve) => {
      try {
        const req = op(db.transaction(storeName, mode).objectStore(storeName));
        req.onsuccess = () => resolve((req.result as T) ?? null);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  };
  return {
    getItem: async (key) => {
      const v = await run<unknown>("readonly", (s) => s.get(key));
      return typeof v === "string" ? v : null;
    },
    setItem: async (key, value) => {
      await run("readwrite", (s) => s.put(value, key));
    },
    removeItem: async (key) => {
      await run("readwrite", (s) => s.delete(key));
    },
  };
}

export type PersistOptions = {
  storage: AsyncStorage<string> | null;
  /** The storage key (one per API origin, so two backends never share a cache). */
  key: string;
  throttleMs?: number;
};

/** The signed-in user's id, from the cached `/me`. */
const ownerOf = (queryClient: QueryClient): string | null => {
  const me = queryClient.getQueryData<{ user?: { id?: string } }>(["me"]);
  return me?.user?.id ?? null;
};

/**
 * Starts persisting chat for whoever is signed in: restores their stored cache once `/me` is
 * known, then keeps writing it (throttled). Returns the stop function. A no-op without storage.
 */
export function startChatPersistence(queryClient: QueryClient, o: PersistOptions): () => void {
  if (!o.storage) return () => {};
  const persister = createAsyncStoragePersister({
    storage: o.storage,
    key: o.key,
    throttleTime: o.throttleMs ?? 1000,
    serialize: (client: PersistedClient) =>
      JSON.stringify({ ...client, clientState: trimForPersist(client.clientState) }),
    deserialize: (s: string) => JSON.parse(s) as PersistedClient,
  });
  const dehydrateOptions = {
    shouldDehydrateQuery: (q: Query) => q.state.status === "success" && persistable(q.queryKey),
    shouldDehydrateMutation: () => false,
  };
  let owner: string | null = null;
  let unsubscribe: (() => void) | null = null;
  const follow = () => {
    const next = ownerOf(queryClient);
    if (next === owner) return;
    owner = next;
    unsubscribe?.();
    unsubscribe = null;
    if (!next) return;
    const buster = next;
    void persistQueryClientRestore({
      queryClient,
      persister,
      buster,
      maxAge: PERSIST_MAX_AGE_MS,
    })
      .catch(() => {})
      .finally(() => {
        if (owner !== buster) return;
        unsubscribe = persistQueryClientSubscribe({
          queryClient,
          persister,
          buster,
          dehydrateOptions,
        });
      });
  };
  const off = queryClient.getQueryCache().subscribe((e) => {
    if (e.query.queryKey[0] === "me" && e.query.queryKey.length === 1) follow();
  });
  follow();
  return () => {
    off();
    unsubscribe?.();
  };
}
